/** @jsxImportSource @emotion/react */
import { v4 as uuidv4 } from "uuid";
import { Fragment, useRef, useState } from "react";
import { Button } from "components/atoms/Button.atom";
import { Text } from "components/atoms/Text.atom";
import { Popover, Overlay, InputGroup } from "react-bootstrap";
import { FlexColDiv, FlexDiv } from "styles/container-elements";
import { Checkbox } from "components/atoms/Checkbox.atom";
import DateTimePicker from "react-widgets/lib/DateTimePicker";
import {
  getDateFormatFromUserPreferences,
  getTimeFormatFromUserPreferences,
} from "utils/date-time";
import moment from "moment";
import { MapPreference } from "shared/constants/organization.const";
import { Icon } from "components/atoms/Icon.atom";
import { faSliders } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import Colors from "styles/colors";
import { useMapPreferenceOptionsTranslation } from "shared/hooks/useMapPreferenceHook";

export const MapOptionsPanel = ({
  mapPreference,
  setMapPreference,
  applyDateRangeFilter,
  loaded,
}) => {
  const { t } = useTranslation("components");
  const containerRef = useRef(null);
  const targetRef = useRef(null);

  const { getTranslatedOption } = useMapPreferenceOptionsTranslation();

  const [show, setShow] = useState(false);

  const [stateFromValue, setStateFromValue] = useState(null);
  const [stateToValue, setStateToValue] = useState(null);

  const value = {};
  const fromValue = value?.from ?? null;
  const toValue = value?.to ?? null;
  const fromValueDate = fromValue ? new Date(fromValue) : null;
  const toValueDate = toValue ? new Date(toValue) : null;
  const dateFormat = getDateFormatFromUserPreferences(); // : "l"
  const timeFormat = getTimeFormatFromUserPreferences();
  const dateTimeFormat = dateFormat + " " + timeFormat;

  const onChange = (selected, value) => {
    const mergedValue = {
      ...mapPreference,
      [selected]: {
        ...mapPreference[selected],
        showCoordinatesOnMap: value,
      },
    };
    setMapPreference(mergedValue);
  };

  return (
    <Fragment>
      <div
        ref={containerRef}
        css={{
          position: "absolute",
          top: 40,
          left: 5,
          zIndex: 2,
        }}
      >
        <Overlay
          show={show}
          placement="bottom"
          containerPadding={20}
          target={targetRef}
          container={containerRef.current}
          rootClose={true}
          onHide={() => setShow(false)}
        >
          <Popover
            style={{
              padding: "1.2em",
              maxWidth: "100%",
              marginTop: "8px",
            }}
          >
            <FlexDiv style={{ gap: "2em" }}>
              <FlexColDiv>
                <Text bold>{t("components:Display")}:</Text>
                {Object.keys(MapPreference).map((item) => {
                  const preference = mapPreference[item];
                  if (!preference.display) {
                    return null;
                  }

                  const inputId = item.toLowerCase();
                  const disabled = !preference.enableCheckbox;

                  return (
                    <InputGroup key={uuidv4()}>
                      <Checkbox
                        id={inputId}
                        checked={preference.showCoordinatesOnMap && !disabled}
                        onChange={(value) => {
                          onChange(item, value);
                        }}
                        disabled={disabled}
                        disabledColor={Colors.text.DISABLED}
                      />
                      <Text
                        as="label"
                        htmlFor={inputId}
                        style={{
                          cursor: disabled ? "default" : "pointer",
                          marginBottom: 0,
                          color: disabled
                            ? Colors.text.DISABLED
                            : Colors.text.BLACK,
                        }}
                      >
                        {getTranslatedOption(item)}
                      </Text>
                    </InputGroup>
                  );
                })}
              </FlexColDiv>
              {/* temporary disabled until validated */}
              <FlexColDiv style={{ display: "none" }}>
                <div css={{ marginBottom: "1em" }}>
                  <Text>{t("components:From")}:</Text>
                  <DateTimePicker
                    style={{ minWidth: "17em" }}
                    defaultValue={fromValueDate}
                    defaultCurrentDate={moment().startOf("day").toDate()}
                    max={toValueDate ?? undefined}
                    onChange={(val) => {
                      setStateFromValue(val);
                    }}
                    format={dateTimeFormat}
                    timeFormat={timeFormat}
                  />
                </div>
                <div>
                  <Text>{t("components:To")}:</Text>
                  <DateTimePicker
                    key={fromValueDate}
                    style={{ minWidth: "17em" }}
                    defaultValue={toValueDate}
                    defaultCurrentDate={
                      fromValueDate ?? moment().startOf("day").toDate()
                    }
                    min={fromValueDate ?? undefined}
                    onChange={(val) => {
                      setStateToValue(val);
                    }}
                    format={dateTimeFormat}
                    timeFormat={timeFormat}
                  />
                </div>
                <div>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      // Format values as ISO strings
                      const from = stateFromValue
                        ? moment(stateFromValue).toISOString()
                        : null;
                      const to = stateToValue
                        ? moment(stateToValue).toISOString()
                        : null;

                      applyDateRangeFilter(from, to);
                    }}
                    css={{ marginTop: "1em", width: "100%" }}
                  >
                    {t("components:Apply Date Filter")}
                  </Button>
                </div>
              </FlexColDiv>
            </FlexDiv>
          </Popover>
        </Overlay>
      </div>
      <Button
        variant="primary"
        size="sm"
        onClick={() => {
          setShow(!show);
        }}
        css={{
          position: "absolute",
          top: 5,
          left: 5,
          zIndex: 2,
        }}
        disabled={!loaded}
      >
        <Icon src={faSliders} style={{ marginLeft: 5, marginRight: 5 }} />
        <Text>{t("components:Options")}</Text>
      </Button>
    </Fragment>
  );
};

MapOptionsPanel.propTypes = {
  mapPreference: PropTypes.object,
  setMapPreference: PropTypes.func,
  applyDateRangeFilter: PropTypes.func,
  loaded: PropTypes.bool,
};
